/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

// @components
import PeopleServiceCard from "../../../pages/asCompany/servicesItem/components/PeopleServiceCard";
import ServiceDetailsProfessional from "../../../pages/asCompany/servicesItem/components/ServiceDetailsProfessional";

// @store
import useServiceRequestStore from "../../../store/useServiceRequestStore";
import { useSpinnerStore } from "../../../store/useSpinnerStore";
import { useCompanyStore } from "../../../store/useCompanyStore";

// @types
import { IParamsServiceReques, IServiceById, RequestFilters, ServiceRequest } from "../../../types/request.types";
import emptyIcon from "../../../assets/icons/not_services.svg";

// @services
import servicesService from "../../../services/request.service";

const ServicesDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { openSpinner } = useSpinnerStore();
  const { data } = useCompanyStore();
  const {
    isLoading,
    requestListByStatus,
    serviceList,
    fetchActiveForProfessional,
    activeForProfessional
  } = useServiceRequestStore();
  const [itemSelected, setItemSelected] = useState<IServiceById | null>();

  useEffect(() => {
    if (isLoading) return openSpinner(true);
    return openSpinner(false);
  }, [isLoading])

  useEffect(() => {
    const params: IParamsServiceReques = {
      statusList: [2, 3],
      categoryId: data?.category.categoryId ?? '',
      createdByMe: false
    }
    requestListByStatus(params);
  }, [requestListByStatus]);

  useEffect(() => {
    const params: RequestFilters = {
      pageNumber: 1,
      pageSize: 100
    }
    fetchActiveForProfessional(params);
  }, [fetchActiveForProfessional]);

  const activeServices = activeForProfessional?.items?.filter(
    service => service.serviceRequestStatus === 'ACTIVE' || service.serviceRequestStatus === 'PENDING_COMPLETED'
  ) || [];

  const completedServices = serviceList?.items?.filter(
    service => service.serviceRequestStatus === 2
  ) || [];

  const cancelledServices = serviceList?.items?.filter(
    service => service.serviceRequestStatus === 3
  ) || [];

  const withoutServices = (text: string) => {
    return (
      <div className="flex text-center flex-col gap-[30px]">
        <img
          src={emptyIcon}
          alt="Aun nsin servicios"
          width={150}
          className="mx-auto"
        />
        <h6 className="m-0 text-[15px] font-bold dark:text-dark-cText/80">
          {t("professional.services.withoutServices")}
        </h6>
        <p className="m-0 text-gray-400 text-[14px] dark:text-dark-cText/60">
          {text}
        </p>
      </div>
    )
  }

  const getItemSelected = async (id: string) => {
    openSpinner(true);
    try {
      const response = await servicesService.getServiceById(id ?? '');
      setItemSelected(response);
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.callList"));
    }
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr,1fr,1fr] gap-6 p-6 h-screen overflow-auto mb-20">
      {/* Primera columna */}
      <div className="col-span-1 space-y-6 flex-1 min-h-[80vh] max-h-[100vh] border-box overflow-y-auto w-full">
        {/* Información general */}
        <div className="bg-white dark:bg-dark-cBackground rounded-lg shadow-md p-8 w-full">
          <h2 className="text-[14px] font-medium mb-4 dark:text-dark-cText">
            {t("professional.services.generalInformation")}
          </h2>
          <div className="flex flex-col sm:flex-row justify-between w-full gap-[10px]">
            <div className="flex flex-col items-center justify-center bg-bgSuccess text-textOnSuccess rounded-2xl py-4 w-full sm:w-[100px]">
              <p className="text-xl font-bold">{activeServices?.length || 0}</p>
              <p className="text-sm">{t("professional.services.active")}</p>
            </div>

            <div className="flex flex-col items-center justify-center bg-bgSuccess text-textOnSuccess rounded-2xl py-4 w-full sm:w-[100px]">
              <p className="text-xl font-bold">{completedServices?.length || 0}</p>
              <p className="text-sm">{t("professional.services.executed")}</p>
            </div>

            <div className="flex flex-col items-center justify-center bg-bgSuccess text-textOnSuccess rounded-2xl py-4 w-full sm:w-[100px]">
              <p className="text-xl font-bold">{cancelledServices?.length || 0}</p>
              <p className="text-sm">{t("professional.services.cancel")}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col flex-1 min-h-[64vh] bg-white dark:bg-dark-cBackground rounded-lg shadow-md p-5 overflow-auto w-full">
          <h2 className="text-[14px] font-semibold mb-[50px] text-center dark:text-dark-cText">
            {t("professional.services.activeServices")}
          </h2>
          <div className="flex flex-col gap-[10px] w-full">
            {activeServices?.length
              ? activeServices?.map((service: ServiceRequest) => (
                <PeopleServiceCard
                  key={service.serviceRequestId}
                  service={service}
                  onClick={() => { getItemSelected(service.serviceRequestId) }}
                />
              ))
              : (withoutServices(t("professional.services.withoutActiveServices")))
            }
          </div>
        </div>
      </div>

      {/* Segunda columna */}
      <div className="col-span-1 bg-white dark:bg-dark-cBackground rounded-lg shadow-md p-5 flex-1 min-h-[80vh] max-h-[100vh] overflow-auto">
        <h2 className="text-[14px] font-semibold mt-8 mb-[30px] text-center dark:text-dark-cText">
          {t("professional.services.executedServices")}
        </h2>
        <div className="space-y-4 overflow-y-auto max-h-100">
          {completedServices?.length
            ? completedServices?.map((service: ServiceRequest) => (
              <PeopleServiceCard
                key={service?.serviceRequestId}
                service={service}
                onClick={() => { getItemSelected(service.serviceRequestId) }}
              />
            ))
            : <div className="h-[90%] pt-[30%]">
              {withoutServices(t("professional.services.withoutExecutedServices"))}
            </div>
          }
        </div>
      </div>

      {/* Tercera columna */}
      <div className="col-span-1 bg-white dark:bg-dark-cBackground overflow-y-auto rounded-lg shadow-md p-4 flex flex-col min-h-[80vh] max-h-[100vh] xl:min-h-[50vh]">
        {itemSelected
          ? <ServiceDetailsProfessional
            itemSelected={itemSelected}
            setItemSelected={setItemSelected}
          />
          : <div className="flex justify-center py-auto flex-1 lg:mx-4 mx-0">
            <p className="text-[14px] font-medium flex text-gray-400 dark:text-dark-cText text-center my-auto">
              {t("professional.services.selectService")}
            </p>
          </div>
        }
      </div>
    </div>
  );
};

export default ServicesDashboard;
